import { cn } from '../../lib/cn'
import { GameArt } from './GameArt'

interface GameTileProps {
  appid: number
  name: string | null
  artSrc?: string | null
  caption?: string
  href?: string
  onClick?: () => void
  className?: string
}

const focusRing =
  'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ember-400 focus-visible:ring-offset-2 focus-visible:ring-offset-canvas'

/**
 * Cover-art tile: art + truncated title + faint caption (design-system §6.2).
 * Link/button/static depending on href/onClick. Playtime captions are neutral
 * stats, never rankings.
 */
export function GameTile({ appid, name, artSrc, caption, href, onClick, className }: GameTileProps) {
  const body = (
    <>
      <GameArt
        appid={appid}
        src={artSrc}
        name={name}
        className="aspect-[460/215] w-full transition group-hover:ring-ember-400/50"
      />
      <p className="mt-2 truncate text-sm font-medium text-text" title={name ?? undefined}>
        {name ?? `App ${appid}`}
      </p>
      {/* Playtime stat: mono numerals (§3, aligns across the grid) at the muted
          tier — faint fails 4.5:1 for real content (WCAG 1.4.3). */}
      {caption && <p className="font-mono text-xs tabular-nums text-text-muted">{caption}</p>}
    </>
  )
  const shared = cn('block w-full rounded-card text-left', className)
  // `group` (art hover ring) + focus ring are interactive affordances — only
  // the link/button variants get them; the static tile must not signal
  // clickability (amber is meaning).
  const interactive = cn('group', focusRing, shared)

  if (href) {
    return (
      <a href={href} target="_blank" rel="noopener noreferrer" className={interactive}>
        {body}
        <span className="sr-only"> (opens in new tab)</span>
      </a>
    )
  }
  if (onClick) {
    return (
      <button type="button" onClick={onClick} className={interactive}>
        {body}
      </button>
    )
  }
  return <div className={cn(shared, 'cursor-default')}>{body}</div>
}
